import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { ApiClientError } from '../../api/client'
import { knowledgeApi } from '../../api/knowledge'
import {
  type CompileResult,
  promptLibraryApi,
  type ResolvedPromptEntry,
  type ResolvedPromptsView,
} from '../../api/prompt-library'
import { promptsApi, type SavedPrompt } from '../../api/prompts'
import type { KnowledgeArea } from '../../pages/knowledge/knowledgeTypes'
import { useDialog } from '../dialogs/useDialog'
import { PromptList } from './PromptList'
import PromptMarkdown from './PromptMarkdown'

interface LegacyProjectPromptsTabProps {
  projectId: number
  canManage: boolean
}

type ScopeFilter = { areaId: number | null }

function isLibraryMissing(error: unknown): boolean {
  return error instanceof ApiClientError && error.status === 404
}

function Section({
  title,
  hint,
  entries,
  children,
}: {
  title: string
  hint: string
  entries: ResolvedPromptEntry[]
  children: (entry: ResolvedPromptEntry) => React.ReactNode
}) {
  if (entries.length === 0) return null
  return (
    <div className="border-t border-harbor-border first:border-t-0">
      <div className="px-6 pt-5">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-harbor-text-muted">
          {title} ({entries.length})
        </h3>
        <p className="mt-1 text-xs text-harbor-text-muted">{hint}</p>
      </div>
      <div className="divide-y divide-harbor-border">
        {entries.map((entry) => (
          <article key={entry.id} className="px-6 py-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h4 className="text-base font-semibold text-harbor-text">
                  {entry.name}
                  {entry.isProduct && (
                    <span className="ml-2 rounded-md border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 align-middle font-mono text-xs text-sky-400">
                      built-in
                    </span>
                  )}
                </h4>
                <p className="mt-1 font-mono text-xs text-harbor-text-muted">
                  {entry.slug} · v{entry.version ?? '?'}
                  {entry.fingerprint && ` · ${entry.fingerprint.slice(0, 12)}`}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">{children(entry)}</div>
            </div>
            {entry.body !== null ? (
              <div className="mt-4">
                <PromptMarkdown markdown={entry.body} maxHeightClass="max-h-36" />
              </div>
            ) : (
              <p className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300">
                {entry.compileNote === 'DISABLED'
                  ? 'Disabled at this scope.'
                  : `Needs variables before it can compile (${entry.compileNote ?? 'unknown reason'}). Preview to supply them.`}
              </p>
            )}
          </article>
        ))}
      </div>
    </div>
  )
}

function ActionButton({
  label,
  onClick,
  danger,
  disabled,
}: {
  label: string
  onClick: () => void
  danger?: boolean
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        danger === true
          ? 'border-red-500/40 text-red-300 hover:border-red-500/70'
          : 'border-harbor-border text-harbor-text hover:border-emerald-500/50 hover:text-emerald-400'
      }`}
    >
      {label}
    </button>
  )
}

export function LegacyProjectPromptsTab({ projectId, canManage }: LegacyProjectPromptsTabProps) {
  const [view, setView] = useState<ResolvedPromptsView | null>(null)
  const [legacy, setLegacy] = useState<SavedPrompt[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [scope, setScope] = useState<ScopeFilter>({ areaId: null })
  const [areas, setAreas] = useState<KnowledgeArea[]>([])
  const [customizing, setCustomizing] = useState<string | null>(null)
  const [customText, setCustomText] = useState('')
  const [previewId, setPreviewId] = useState<string | null>(null)
  const [preview, setPreview] = useState<CompileResult | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewVars, setPreviewVars] = useState('')
  const [busy, setBusy] = useState(false)
  const dialog = useDialog()

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const next = await promptLibraryApi.resolvedView(projectId, scope.areaId ?? undefined)
      setView(next)
      setLegacy(null)
    } catch (e) {
      // Library disabled (flag off → 404): fall back to the legacy list.
      if (isLibraryMissing(e)) {
        try {
          setLegacy(await promptsApi.list(projectId))
          setView(null)
        } catch (fallbackError) {
          setError(
            fallbackError instanceof Error ? fallbackError.message : 'Failed to load prompts'
          )
        }
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load prompts')
      }
    } finally {
      setLoading(false)
    }
  }, [projectId, scope.areaId])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    let cancelled = false
    void knowledgeApi
      .listAreas(projectId)
      .then(({ areas: next }) => {
        if (!cancelled) setAreas(next)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [projectId])

  const runAction = useCallback(
    async (label: string, fn: () => Promise<unknown>) => {
      setBusy(true)
      try {
        await fn()
        toast.success(label)
        await load()
      } catch (e) {
        toast.error(e instanceof Error ? e.message : `${label} failed`)
      } finally {
        setBusy(false)
      }
    },
    [load]
  )

  const inherit = (entry: ResolvedPromptEntry) =>
    runAction('Inherited for this project', () =>
      promptLibraryApi.upsertBinding(entry.id, {
        scopeType: 'PROJECT',
        scopeId: projectId,
        inheritanceMode: 'append',
      })
    )

  const disable = async (entry: ResolvedPromptEntry) => {
    const confirmed = await dialog.confirm({
      title: `Disable "${entry.name}" for this project?`,
      description: 'Descendant scopes re-resolve without it.',
      confirmLabel: 'Disable',
      tone: 'danger',
    })
    if (!confirmed) return
    void runAction('Disabled for this project', () =>
      promptLibraryApi.upsertBinding(entry.id, {
        scopeType: 'PROJECT',
        scopeId: projectId,
        inheritanceMode: 'disable',
      })
    )
  }

  const saveCustomization = (entry: ResolvedPromptEntry) => {
    if (customText.trim().length === 0) {
      toast.error('Customization text is empty')
      return
    }
    void runAction('Customization saved', () =>
      promptLibraryApi.upsertBinding(entry.id, {
        scopeType: 'PROJECT',
        scopeId: projectId,
        inheritanceMode: 'append',
        instructionsOverride: customText,
      })
    ).finally(() => {
      setCustomizing(null)
      setCustomText('')
    })
  }

  const removeCustomization = (entry: ResolvedPromptEntry) => {
    const bindingId = scope.areaId !== null ? entry.areaBindingId : entry.projectBindingId
    if (!bindingId) {
      toast.error('No local binding to remove')
      return
    }
    void runAction('Customization removed', () =>
      promptLibraryApi.patchBinding(entry.id, bindingId, { enabled: false })
    )
  }

  const openPreview = async (entry: ResolvedPromptEntry) => {
    setPreviewId(entry.id)
    setPreview(null)
    setPreviewLoading(true)
    try {
      let variables: Record<string, unknown> | undefined
      if (previewVars.trim().length > 0) {
        variables = JSON.parse(previewVars) as Record<string, unknown>
      }
      const result = await promptLibraryApi.compilePreview(entry.id, {
        projectId,
        ...(scope.areaId !== null ? { knowledgeAreaId: scope.areaId } : {}),
        ...(variables !== undefined ? { variables } : {}),
      })
      setPreview(result)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Preview failed')
      setPreviewId(null)
    } finally {
      setPreviewLoading(false)
    }
  }

  if (loading && view === null && legacy === null) {
    return (
      <p className="px-6 py-10 text-center text-sm text-harbor-text-secondary">Loading prompts…</p>
    )
  }
  if (error !== null) {
    return (
      <div className="px-6 py-10 text-center">
        <p className="text-sm font-medium text-red-300">{error}</p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-4 rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 text-sm font-medium text-harbor-text transition-colors hover:border-emerald-500/50 hover:text-emerald-400"
        >
          Retry
        </button>
      </div>
    )
  }
  if (legacy !== null) {
    return <PromptList prompts={legacy} />
  }
  if (view === null) return null

  return (
    <div>
      {areas.length > 0 && (
        <div className="flex items-center gap-3 border-b border-harbor-border px-6 py-4">
          <label htmlFor="prompt-scope" className="text-xs font-medium text-harbor-text-muted">
            Scope
          </label>
          <select
            id="prompt-scope"
            value={scope.areaId ?? ''}
            onChange={(e) =>
              setScope({ areaId: e.target.value === '' ? null : Number(e.target.value) })
            }
            className="rounded-lg border border-harbor-border bg-harbor-surface-raised px-2.5 py-1.5 text-sm text-harbor-text"
          >
            <option value="">Project-wide</option>
            {areas.map((area) => (
              <option key={area.id} value={area.id}>
                {'— '.repeat(Math.min(area.path.split('/').filter(Boolean).length, 4))}
                {area.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <Section
        title="Inherited"
        hint="Flows from organization, product, or ancestor scopes. Inherit to pin a project copy of the behavior."
        entries={view.inherited}
      >
        {(entry) => (
          <>
            <ActionButton label="Preview" onClick={() => void openPreview(entry)} />
            {canManage && (
              <ActionButton label="Inherit" onClick={() => void inherit(entry)} disabled={busy} />
            )}
          </>
        )}
      </Section>
      <Section title="Local" hint="Customized or pinned at this scope." entries={view.local}>
        {(entry) => (
          <>
            <ActionButton label="Preview" onClick={() => void openPreview(entry)} />
            {canManage && (
              <>
                <ActionButton
                  label="Customize"
                  onClick={() => {
                    setCustomizing(customizing === entry.id ? null : entry.id)
                    setCustomText('')
                  }}
                  disabled={busy}
                />
                <ActionButton
                  label="Disable"
                  danger
                  onClick={() => disable(entry)}
                  disabled={busy}
                />
                {(entry.projectBindingId !== null || entry.areaBindingId !== null) && (
                  <ActionButton
                    label="Remove"
                    danger
                    onClick={() => removeCustomization(entry)}
                    disabled={busy}
                  />
                )}
              </>
            )}
          </>
        )}
      </Section>
      <Section
        title="Disabled"
        hint="Explicitly disabled at this scope; nothing compiles from these."
        entries={view.disabled}
      >
        {(entry) => (
          <>
            {canManage && entry.projectBindingMode !== 'disable' && (
              <ActionButton label="Disable" danger onClick={() => disable(entry)} disabled={busy} />
            )}
            {canManage && (
              <ActionButton label="Re-enable" onClick={() => void inherit(entry)} disabled={busy} />
            )}
          </>
        )}
      </Section>
      {view.inherited.length === 0 && view.local.length === 0 && view.disabled.length === 0 && (
        <div className="px-6 py-12 text-center">
          <h3 className="text-base font-semibold text-harbor-text">No prompts at this scope</h3>
          <p className="mx-auto mt-2 max-w-lg text-sm text-harbor-text-secondary">
            Agents can add reusable instructions with the{' '}
            <code className="font-mono text-emerald-400">save_prompt</code> MCP tool, or curate
            shared prompts in the organization library.
          </p>
        </div>
      )}
      {customizing !== null && (
        <div className="border-t border-harbor-border px-6 py-5">
          <h4 className="text-sm font-semibold text-harbor-text">Project customization</h4>
          <p className="mt-1 text-xs text-harbor-text-muted">
            Appended after inherited layers. Leave empty and save nothing to cancel.
          </p>
          <textarea
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            rows={5}
            className="mt-3 w-full rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 font-mono text-sm text-harbor-text"
            placeholder="Project-specific instructions…"
          />
          <div className="mt-3 flex gap-2">
            <ActionButton
              label="Save customization"
              onClick={() => {
                const entry = [...view.local, ...view.inherited].find((e) => e.id === customizing)
                if (entry) saveCustomization(entry)
              }}
              disabled={busy}
            />
            <ActionButton
              label="Cancel"
              onClick={() => {
                setCustomizing(null)
                setCustomText('')
              }}
            />
          </div>
        </div>
      )}
      {previewId !== null && (
        <div className="border-t border-harbor-border px-6 py-5">
          <h4 className="text-sm font-semibold text-harbor-text">Effective preview</h4>
          {previewLoading ? (
            <p className="mt-2 text-sm text-harbor-text-secondary">Compiling…</p>
          ) : preview !== null ? (
            <div className="mt-3 space-y-3">
              <div className="flex flex-wrap gap-1.5">
                {preview.layers.map((layer, i) => (
                  <span
                    key={`${layer.scopeType}:${layer.scopeId ?? 'x'}:${i}`}
                    className="rounded-md border border-harbor-border bg-harbor-surface-raised px-2 py-0.5 font-mono text-xs text-harbor-text-secondary"
                    title={`mode=${layer.mode} revision=${layer.bindingRevision ?? 'n/a'}`}
                  >
                    {layer.scopeType}:{layer.scopeName ?? layer.scopeId ?? 'runtime'} [{layer.mode}]
                  </span>
                ))}
              </div>
              {preview.effectiveInstructions ? (
                <PromptMarkdown
                  markdown={preview.effectiveInstructions}
                  maxHeightClass="max-h-64"
                />
              ) : (
                <p className="rounded-lg border border-harbor-border bg-harbor-surface-raised/60 px-4 py-3 font-mono text-sm text-harbor-text-secondary">
                  (no body — disabled)
                </p>
              )}
              <p className="font-mono text-xs text-harbor-text-muted">
                tokens≈{preview.estimatedTokens.total} (instructions≈
                {preview.estimatedTokens.instructions}) · {preview.compilerVersion} ·{' '}
                {preview.compilationFingerprint.slice(0, 16)}…
              </p>
              {preview.warnings.length > 0 && (
                <ul className="list-disc pl-5 text-xs text-amber-300">
                  {preview.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={() => {
                  setPreviewId(null)
                  setPreview(null)
                }}
                className="rounded-lg border border-harbor-border px-2.5 py-1.5 text-xs font-medium text-harbor-text hover:border-emerald-500/50 hover:text-emerald-400"
              >
                Close preview
              </button>
            </div>
          ) : null}
          <details className="mt-3">
            <summary className="cursor-pointer text-xs text-harbor-text-muted">
              Variables (JSON, optional)
            </summary>
            <textarea
              value={previewVars}
              onChange={(e) => setPreviewVars(e.target.value)}
              rows={2}
              placeholder='{"review_focus": "steel pricing"}'
              className="mt-2 w-full rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 font-mono text-xs text-harbor-text"
            />
          </details>
        </div>
      )}
    </div>
  )
}
